import { LRLanguage } from '@codemirror/language';
import { styleTags, tags as t } from '@lezer/highlight';
import { Tree, SyntaxNode, NodeWeakMap } from '@lezer/common';
import { LRParser } from '@lezer/lr';
import { parser } from './generated/grammar';
import * as Terms from './generated/grammar.terms';
import {
  ASTBinary,
  ASTBoolean,
  ASTCall,
  ASTComparison,
  ASTExpr,
  ASTIdentifier,
  ASTKwarg,
  ASTNull,
  ASTNumber,
  ASTProgram,
  ASTStmt,
  ASTString,
  ASTUnary,
} from './ast';
import { UserInputError } from '@watershed/errors/UserInputError';
import {
  divideUnits,
  exponentiateUnit,
  getUnitBang,
  multiplyUnits,
  Unit,
} from '../unit';
import { ExprType } from './expr_types';
import { WatershedError } from '@watershed/errors/WatershedError';
import {
  getNodeChildren,
  ParseContext,
  ParseError,
  ParserPosition,
} from '../utils/parserUtils';

const exprParser: LRParser = parser.configure({
  top: 'Expression',
  props: [
    styleTags({
      Variable: t.variableName,
      Property: t.propertyName,
      Boolean: t.bool,
      Number: t.number,
      String: t.string,
      BinaryOp: t.arithmeticOperator,
      CompareOp: t.compareOperator,
      Function: t.function(t.variableName),
      Null: t.null,
      '( )': t.paren,
    }),
  ],
});

export const exprLanguage = LRLanguage.define({
  parser: exprParser,
  languageData: {
    commentTokens: { line: '//' },
  },
});

function parse(top: 'Program' | 'Expression' | 'Unit', str: string): Tree {
  try {
    return parser
      .configure({
        top,
        // "strict" here means "throw an error when the syntax does not match." The
        // parser has an alternate more forgiving mode it uses to guess what you
        // mean in an editor context; we don't want that here.
        strict: true,
      })
      .parse(str);
  } catch (e) {
    throw new UserInputError(`Parse error: unable to parse as ${top}`, {
      cause: e,
      data: { source: 'lscript.parser.parse' },
    });
  }
}

export function isTopLevelParseError(error: unknown): boolean {
  return (
    error instanceof WatershedError &&
    error.message.startsWith('Parse error') &&
    error.data?.source === 'lscript.parser.parse'
  );
}

export function parseProgram(str: string): ASTProgram {
  const tree = parse('Program', str);
  const ctx = new ParseContext(str);
  const children = getNodeChildren(tree.topNode);
  return children.map((line) => _statement(ctx, line));
}

export function parseExpression(str: string): ASTExpr {
  const tree = parse('Expression', str);
  const ctx = new ParseContext(str);
  const children = getNodeChildren(tree.topNode);
  if (children.length !== 1) {
    ctx.impossible('top-level expression', tree.topNode);
  }
  return _expression(ctx, children[0]);
}

export function parseUnit(str: string): Unit {
  const tree = parse('Unit', str);
  const ctx = new ParseContext(str);
  const children = getNodeChildren(tree.topNode);
  if (children.length !== 1) {
    ctx.impossible('top-level unit', tree.topNode);
  }
  return _unit(ctx, children[0]);
}

// TODO: do program lines need to have newlines between them?

// In an interactive editing context, most changes (which are very frequent, and
// need to be interactive to a human editor) don't change most expressions.
// Lezer is carefully coded to allow for this incremental use case, and gives us
// a way to hook our AST conversion code into this incremental / caching
// solution.
//
// https://lezer.codemirror.net/docs/ref/#common.NodeWeakMap
const stmtParseCache = new NodeWeakMap<ASTStmt>();
const exprParseCache = new NodeWeakMap<ASTExpr>();
const unitParseCache = new NodeWeakMap<Unit>();

// We want to allow folks to, given an AST node, look up parse information about
// that node if it's available. Normally, that would take the form of an
// optional property on every AST node. But that solution has a major downside,
// which is that the information can be wrong, unless every piece of code that
// manipulates ASTs is aware of how to properly propagate the state. For
// instance, you can use object spread notation to create a variant of an
// existing AST node:
//
//     const add = l`3+4`;
//     console.log(add.position); // string: "3+4", pos: 0-3
//     const add2 = {
//       ...add,
//       l: l`99`,
//     };
//     console.log(add2.position); // needs to be undefined, but how?
//
// The solution we use here relies on a weak map. Since AST nodes are immutable,
// we know that every time we're passed the same reference, it's the same node,
// and nothing has been done to it. In order to modify a property on an AST
// node, a *new* node has to be created, resulting in a *different* object. And
// making the data structure a weak map (as opposed to a normal map) allows this
// code to have the same garbage-collection behavior as a normal property would
// (i.e., the ParserPosition objects get cleaned up when the AST nodes do)
const astToPosition = new WeakMap<ASTExpr | ASTStmt, ParserPosition>();

// For AST nodes created by this parser (as opposed to e.g., ones constructed
// in-memory), return their position in the input.
export function position(ast: ASTExpr | ASTStmt): ParserPosition | undefined {
  return astToPosition.get(ast);
}

// get a string representation of the expresion
export function stringPosition(ast: ASTExpr | ASTStmt): string {
  const pos = position(ast);
  return pos ? `${pos.input}_${pos.from}_${pos.to}` : '';
}

function _statement(ctx: ParseContext, node: SyntaxNode): ASTStmt {
  let stmt: ASTStmt | undefined = stmtParseCache.get(node);
  if (stmt !== undefined) {
    return stmt;
  }

  const children = getNodeChildren(node);
  switch (children.length) {
    case 2: {
      const [varName, expr] = children;
      stmt = {
        t: 'assign',
        name: ctx.slice(varName),
        expr: _expression(ctx, expr),
      };
      break;
    }
    case 3: {
      const [varName, typeDecl, expr] = children;
      stmt = {
        t: 'assign',
        name: ctx.slice(varName),
        expr: _expression(ctx, expr),
        typeDeclaration: _typeDecl(ctx, typeDecl),
      };
      break;
    }
    default:
      ctx.impossible('statement child count', node);
  }

  stmtParseCache.set(node, stmt);
  astToPosition.set(stmt, ctx.position(node));
  return stmt;
}

function _typeDecl(ctx: ParseContext, node: SyntaxNode): ExprType {
  switch (node.type.id) {
    case Terms.StringType:
      return { ty: 'string' };
    case Terms.BooleanType:
      return { ty: 'boolean' };
    case Terms.DateType:
      return { ty: 'date' };
    case Terms.DatetimeType:
      return { ty: 'datetime' };
    case Terms.NullableStringType:
      return { ty: 'nullable', baseTy: { ty: 'string' } };
    case Terms.NullableBooleanType:
      return { ty: 'nullable', baseTy: { ty: 'boolean' } };
    case Terms.NullableDateType:
      return { ty: 'nullable', baseTy: { ty: 'date' } };
    case Terms.NullableDatetimeType:
      return { ty: 'nullable', baseTy: { ty: 'datetime' } };
    case Terms.NullableNumberType:
    case Terms.NumberType: {
      const children = getNodeChildren(node);
      switch (children.length) {
        case 0:
          if (node.type.id === Terms.NumberType) {
            return { ty: 'number' };
          } else {
            return { ty: 'nullable', baseTy: { ty: 'number' } };
          }
        case 1:
          if (node.type.id === Terms.NumberType) {
            return {
              ty: 'number',
              unit: _unit(ctx, children[0]),
            };
          } else {
            return {
              ty: 'nullable',
              baseTy: {
                ty: 'number',
                unit: _unit(ctx, children[0]),
              },
            };
          }
        default:
          ctx.impossible('number type', node);
      }
    }
    default:
      ctx.impossible('type decl', node);
  }
}

function _unit(ctx: ParseContext, node: SyntaxNode): Unit {
  let unit: Unit | undefined = unitParseCache.get(node);
  if (unit) {
    return unit;
  }

  // This code is a little more permissive than the actual syntax. It's fine, in
  // the sense that the syntax will prohibit things we want to prohibit; this
  // recursive way of writing things is just soooo much easier.
  switch (node.type.id) {
    case Terms.UnitDiv: {
      const children = getNodeChildren(node);
      if (children.length !== 2) {
        ctx.impossible('unit division', node);
      }
      unit = divideUnits(_unit(ctx, children[0]), _unit(ctx, children[1]));
      break;
    }
    case Terms.AtomicUnit:
      return getUnitBang(ctx.slice(node));
    case Terms.UnitMul: {
      const children = getNodeChildren(node);
      if (children.length !== 2) {
        ctx.impossible('unit multiplication', node);
      }
      unit = multiplyUnits(_unit(ctx, children[0]), _unit(ctx, children[1]));
      break;
    }
    case Terms.UnitPow: {
      const children = getNodeChildren(node);
      if (children.length !== 2) {
        ctx.impossible('unit pow', node);
      }
      unit = exponentiateUnit(
        _unit(ctx, children[0]),
        parseInt(ctx.slice(children[1]), 10)
      );
      break;
    }
    default:
      ctx.impossible('unit', node);
  }

  unitParseCache.set(node, unit);
  return unit;
}

function _expression(ctx: ParseContext, node: SyntaxNode): ASTExpr {
  let expr: ASTExpr | undefined = exprParseCache.get(node);
  if (expr !== undefined) {
    return expr;
  }

  switch (node.type.id) {
    case Terms.Boolean:
      expr = _boolean(ctx, node);
      break;
    case Terms.Number:
      expr = _number(ctx, node);
      break;
    case Terms.String:
      expr = _string(ctx, node);
      break;
    case Terms.Null:
      expr = _null(ctx, node);
      break;
    case Terms.Variable:
      expr = _identifier(ctx, node);
      break;
    case Terms.Unary:
      expr = _unary(ctx, node);
      break;
    case Terms.Binary:
      expr = _binary(ctx, node);
      break;
    case Terms.FunctionCall:
      expr = _call(ctx, node);
      break;
    default:
      ctx.impossible('expression', node);
  }

  exprParseCache.set(node, expr);
  astToPosition.set(expr, ctx.position(node));
  return expr;
}

function _boolean(ctx: ParseContext, node: SyntaxNode): ASTBoolean {
  const val = ctx.slice(node);
  switch (val) {
    case 'true':
      return { t: 'boolean', v: true };
    case 'false':
      return { t: 'boolean', v: false };
    default:
      ctx.impossible('boolean', node);
  }
}

function _number(ctx: ParseContext, node: SyntaxNode): ASTNumber {
  const val = ctx.slice(node);
  const num = parseFloat(val);
  if (isNaN(num)) {
    ctx.impossible('number', node);
  }
  return {
    t: 'number',
    v: num,
  };
}

function _string(ctx: ParseContext, node: SyntaxNode): ASTString {
  const val = ctx.slice(node);
  try {
    const str = JSON.parse(val);
    if (typeof str !== 'string') {
      ctx.impossible('string value', node);
    }
    return {
      t: 'string',
      v: str,
    };
  } catch {
    ctx.impossible('string', node);
  }
}

function _null(ctx: ParseContext, node: SyntaxNode): ASTNull {
  return { t: 'null' };
}

function _identifier(ctx: ParseContext, node: SyntaxNode): ASTIdentifier {
  const val = ctx.slice(node);
  return {
    t: 'identifier',
    name: val,
  };
}

function _unary(ctx: ParseContext, node: SyntaxNode): ASTUnary {
  const children = getNodeChildren(node);
  if (children.length !== 2) {
    ctx.impossible('unary', node);
  }
  const [op, expr] = children;
  switch (ctx.slice(op)) {
    case '-':
      return {
        t: 'unary',
        op: '-',
        expr: _expression(ctx, expr),
      };
    default:
      ctx.impossible('unary op', node);
  }
}

function _binary(
  ctx: ParseContext,
  node: SyntaxNode
): ASTBinary | ASTComparison {
  const children = getNodeChildren(node);
  if (children.length !== 3) {
    ctx.impossible('binary', node);
  }
  const [left, op, right] = children;
  const opStr = ctx.slice(op);
  switch (opStr) {
    case '+':
    case '-':
    case '*':
    case '/':
      return {
        t: 'binary',
        op: opStr,
        l: _expression(ctx, left),
        r: _expression(ctx, right),
      };
    case '==':
    case '!=':
    case '>=':
    case '>':
    case '<=':
    case '<':
      return {
        t: 'comparison',
        op: opStr,
        l: _expression(ctx, left),
        r: _expression(ctx, right),
      };
    default:
      ctx.impossible('binary op', node);
  }
}

function _call(ctx: ParseContext, node: SyntaxNode): ASTCall {
  const children = getNodeChildren(node);
  if (children.length < 1) {
    ctx.impossible('function call', node);
  }
  const [name, ...argNodes] = children;

  const args: Array<ASTExpr> = [];
  const kwargs: Map<string, ASTKwarg> = new Map();
  for (const argNode of argNodes) {
    switch (argNode.type.id) {
      case Terms.FunctionArg: {
        const children = getNodeChildren(argNode);
        if (children.length !== 1) {
          ctx.impossible('function positional arg', node);
        }
        args.push(_expression(ctx, children[0]));
        break;
      }
      case Terms.FunctionMatchArg: {
        const children = getNodeChildren(argNode);
        if (children.length !== 3) {
          ctx.impossible('function kwarg', node);
        }
        const [name, kind, expr] = children;
        const nameStr = ctx.slice(name);
        if (kwargs.has(nameStr)) {
          throw new ParseError(`duplicate keyword argument`, ctx, node);
        }
        switch (ctx.slice(kind)) {
          case '=':
            kwargs.set(nameStr, {
              t: 'exact',
              expr: _expression(ctx, expr),
            });
            break;
          case '~':
            kwargs.set(nameStr, {
              t: 'fuzzy',
              expr: _expression(ctx, expr),
            });
            break;
          default:
            ctx.impossible('match kind', node);
        }
        break;
      }
      default:
        ctx.impossible('function arg', node);
    }
  }

  return {
    t: 'call',
    name: ctx.slice(name),
    args,
    kwargs,
  };
}
